import React, { useState, useRef, useEffect } from 'react';
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

type FormType = 'contact' | 'feature-request' | 'bug-report';

const Footer = () => {
  const [formType, setFormType] = useState<FormType>('contact');
  const formRef = useRef<HTMLDivElement>(null);
  const brandTextRef = useRef<HTMLDivElement>(null);
  const [brandTextStyle, setBrandTextStyle] = useState<React.CSSProperties>({
    transform: 'translateY(50vh)',
    opacity: 0,
  });

  const handleFormTypeChange = (type: FormType) => {
    setFormType(type);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (!brandTextRef.current) return;

      const rect = brandTextRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const elementTop = rect.top;
      const elementBottom = rect.bottom;

      // Start showing when element enters viewport from bottom
      const transitionStart = viewportHeight * 1.5;
      const transitionEnd = viewportHeight * 0.5;

      let progress = 0;
      if (elementTop < transitionStart) {
        progress = Math.min(1, Math.max(0,
          (transitionStart - elementTop) / (transitionStart - transitionEnd)
        ));
      }

      const translateY = 20 - (progress * 22);
      const opacity = Math.min(1, progress * 1.5);

      setBrandTextStyle({
        transform: `translateY(${translateY}vh)`,
        opacity: opacity,
      });
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  const getFormTitle = () => {
    switch (formType) {
      case 'feature-request':
        return 'Submit a Feature Request';
      case 'bug-report':
        return 'Report a Bug';
      default:
        return 'Contact Us';
    }
  };

  const getButtonText = () => {
    switch (formType) {
      case 'feature-request':
        return 'Submit Feature Request';
      case 'bug-report':
        return 'Submit Bug Report';
      default:
        return 'Send Message';
    }
  };

  return (
    <>
      <footer id="contact" className="bg-background text-card-foreground border-t border-border relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
        <div ref={formRef} className="text-center dark:border-gray-600">
          <h2 className="text-4xl font-bold mb-8 text-primary">{getFormTitle()}</h2>

          {/* Contact Form */}
          {formType === 'contact' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto min-h-[280px]">
              <div className="flex flex-col items-center">
                <div className="w-full">
                  <Input type="text" placeholder="Name" className="mb-4 bg-background/80 border-border/50" />
                  <Input type="email" placeholder="Email" className="mb-4 bg-background/80 border-border/50" />
                  <Input type="tel" placeholder="Phone" className="bg-background/80 border-border/50" />
                </div>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-full h-full flex flex-col">
                  <Textarea placeholder="Your Message" className="flex-1 min-h-[232px] bg-background/80 border-border/50" />
                </div>
              </div>
            </div>
          )}

          {/* Feature Request Form */}
          {formType === 'feature-request' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto min-h-[280px]">
              <div className="flex flex-col items-center">
                <div className="w-full">
                  <Input type="text" placeholder="Name" className="mb-4 bg-background/80 border-border/50" />
                  <Input type="email" placeholder="Email" className="mb-4 bg-background/80 border-border/50" />
                  <Input type="text" placeholder="Feature Title" className="bg-background/80 border-border/50" />
                </div>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-full h-full flex flex-col gap-4">
                  <Textarea placeholder="Feature Description" className="flex-1 bg-background/80 border-border/50" />
                  <Textarea placeholder="Use Case / Why is this needed?" className="flex-1 bg-background/80 border-border/50" />
                </div>
              </div>
            </div>
          )}

          {/* Bug Report Form */}
          {formType === 'bug-report' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto min-h-[280px]">
              <div className="flex flex-col items-center">
                <div className="w-full">
                  <Input type="text" placeholder="Name" className="mb-4 bg-background/80 border-border/50" />
                  <Input type="email" placeholder="Email" className="mb-4 bg-background/80 border-border/50" />
                  <Input type="text" placeholder="Bug Title" className="bg-background/80 border-border/50" />
                </div>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-full h-full flex flex-col gap-4">
                  <Textarea placeholder="Steps to Reproduce" className="flex-1 bg-background/80 border-border/50" />
                  <Textarea placeholder="Expected Behavior" className="flex-1 bg-background/80 border-border/50" />
                  <Textarea placeholder="Actual Behavior" className="flex-1 bg-background/80 border-border/50" />
                </div>
              </div>
            </div>
          )}

          <Button size="lg" className="mt-8 px-12 bg-primary text-primary-foreground hover:bg-primary/90">
            {getButtonText()}
          </Button>
        </div>

        <hr className="border-t border-border opacity-50 mt-12 mb-8" />

        {/* Footer Links Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {/* Resources */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Resources</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/blog" className="hover:text-primary transition-colors duration-300">Blog</Link></li>
              <li><Link to="/help-center" className="hover:text-primary transition-colors duration-300">Help Center</Link></li>
              <li><Link to="/downloads" className="hover:text-primary transition-colors duration-300">Downloads</Link></li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Support</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>
                <button
                  onClick={() => handleFormTypeChange('contact')}
                  className="hover:text-primary transition-colors duration-300 text-left"
                >
                  Contact Support
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleFormTypeChange('feature-request')}
                  className="hover:text-primary transition-colors duration-300 text-left"
                >
                  Feature Request
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleFormTypeChange('bug-report')}
                  className="hover:text-primary transition-colors duration-300 text-left"
                >
                  Bug Report
                </button>
              </li>
              <li><Link to="/community" className="hover:text-primary transition-colors duration-300">Community Forum</Link></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Legal</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/privacy" className="hover:text-primary transition-colors duration-300">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-primary transition-colors duration-300">Terms & Conditions</Link></li>
            </ul>
          </div>

          {/* Company Info */}
          <div>
            <h3 className="font-semibold text-foreground mb-4">Contact</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>+421 902 691 939</li>
              <li>info@taginn.app</li>
              <li className="pt-2">IČO: 55959652</li>
              <li>DIČ: 1128572753</li>
            </ul>
          </div>
        </div>
      </div>

        {/* Big Brand Text */}
        <div className="relative w-full -mt-12 z-20">
          <div
            ref={brandTextRef}
            className="w-full text-center pointer-events-none"
            style={brandTextStyle}
          >
            <h2
              className="font-bold text-primary whitespace-nowrap leading-none"
              style={{
                fontSize: 'min(12vw, calc(100vw / 8))',
                maxWidth: '100vw',
                margin: '0 auto'
              }}
            >
              TAG-Innovation
            </h2>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
